<script setup lang="ts">
import {
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastRoot,
  ToastTitle,
  ToastViewport,
} from 'reka-ui'
import { computed } from 'vue'

import EIcon from './EIcon.vue'
import { useDirection } from '../lib/direction'
import { useUiStrings } from '../lib/strings'
import { useToast, type ToastRecord } from './toast'
import type { IconName } from './icons'

/**
 * The toast host. Mount once in each app shell, outside the router view.
 *
 * reka-ui's Toast primitives supply the parts that are easy to get wrong: a
 * polite live region, hover and focus pausing the timer, swipe dismissal, and
 * F6 to jump to the viewport from anywhere.
 */

const props = withDefaults(
  defineProps<{
    /** Bottom-end sits clear of the left nav and the right rail. */
    position?: 'top-end' | 'bottom-end' | 'bottom-start'
    closeLabel?: string
  }>(),
  { position: 'bottom-end', closeLabel: '' },
)

const { toasts, dismiss } = useToast()

const strings = useUiStrings()
const direction = useDirection()

const resolvedCloseLabel = computed(() =>
  props.closeLabel === '' ? strings.value.toastDismiss : props.closeLabel,
)

/**
 * Swiping a toast away moves it off the *reading end* of the screen. Under
 * `dir="rtl"` that is the left, so a hard-coded `right` would ask an RTL user
 * to swipe a toast back across the page it came from.
 */
const swipeDirection = computed(() => (direction.value === 'rtl' ? 'left' : 'right'))

const TONE_ICON: Record<string, IconName> = {
  success: 'success',
  danger: 'danger',
  warning: 'alert',
  info: 'info',
  accent: 'info',
  neutral: 'info',
}

function iconFor(toast: ToastRecord): IconName {
  return TONE_ICON[toast.tone] ?? 'info'
}

function onOpenChange(open: boolean, toast: ToastRecord): void {
  if (!open) dismiss(toast.id)
}
</script>

<template>
  <ToastProvider :duration="5000" :swipe-direction="swipeDirection">
    <ToastRoot
      v-for="toast in toasts"
      :key="toast.id"
      class="e-toast"
      :class="`e-toast--${toast.tone}`"
      :duration="toast.durationMs === 0 ? Number.MAX_SAFE_INTEGER : toast.durationMs"
      :type="toast.tone === 'danger' ? 'foreground' : 'background'"
      @update:open="onOpenChange($event, toast)"
    >
      <EIcon :name="iconFor(toast)" :size="16" class="e-toast__icon" />

      <div class="e-toast__body">
        <ToastTitle class="e-toast__title">{{ toast.title }}</ToastTitle>
        <ToastDescription v-if="toast.description !== ''" class="e-toast__description">
          {{ toast.description }}
        </ToastDescription>
      </div>

      <ToastAction
        v-if="toast.action"
        class="e-toast__action"
        :alt-text="toast.action.label"
        @click="toast.action.handler()"
      >
        {{ toast.action.label }}
      </ToastAction>

      <ToastClose class="e-toast__close" :aria-label="resolvedCloseLabel">
        <EIcon name="close" :size="14" />
      </ToastClose>
    </ToastRoot>

    <ToastViewport class="e-toast__viewport" :class="`e-toast__viewport--${position}`" />
  </ToastProvider>
</template>

<style>
.e-toast__viewport {
  position: fixed;
  z-index: var(--erpx-z-toast);
  display: flex;
  flex-direction: column;
  gap: var(--erpx-space-2);
  width: min(24rem, calc(100vw - var(--erpx-space-8)));
  margin: 0;
  padding: var(--erpx-space-4);
  list-style: none;
  outline: none;
}

.e-toast__viewport--top-end {
  inset-block-start: 0;
  inset-inline-end: 0;
}

.e-toast__viewport--bottom-end {
  inset-block-end: 0;
  inset-inline-end: 0;
  flex-direction: column-reverse;
}

.e-toast__viewport--bottom-start {
  inset-block-end: 0;
  inset-inline-start: 0;
  flex-direction: column-reverse;
}

.e-toast {
  display: flex;
  align-items: flex-start;
  gap: var(--erpx-space-2);
  padding: var(--erpx-space-3);
  background-color: var(--erpx-color-surface-overlay);
  border: 1px solid var(--erpx-color-border-subtle);
  border-inline-start: 3px solid var(--erpx-color-neutral-solid);
  border-radius: var(--erpx-radius);
  box-shadow: var(--erpx-shadow-overlay);
  animation: e-toast-in var(--erpx-duration-normal) var(--erpx-easing-standard);
}

.e-toast[data-state='closed'] {
  animation: e-toast-out var(--erpx-duration-fast) var(--erpx-easing-standard);
}

.e-toast--success { border-inline-start-color: var(--erpx-color-success-solid); }
.e-toast--warning { border-inline-start-color: var(--erpx-color-warning-solid); }
.e-toast--danger { border-inline-start-color: var(--erpx-color-danger-solid); }
.e-toast--info { border-inline-start-color: var(--erpx-color-info-solid); }
.e-toast--accent { border-inline-start-color: var(--erpx-color-accent-solid); }

.e-toast--success .e-toast__icon { color: var(--erpx-color-success-solid); }
.e-toast--warning .e-toast__icon { color: var(--erpx-color-warning-solid); }
.e-toast--danger .e-toast__icon { color: var(--erpx-color-danger-solid); }
.e-toast--info .e-toast__icon { color: var(--erpx-color-info-solid); }
.e-toast--accent .e-toast__icon { color: var(--erpx-color-accent-solid); }
.e-toast--neutral .e-toast__icon { color: var(--erpx-color-text-muted); }

.e-toast__icon {
  margin-block-start: 1px;
}

.e-toast__body {
  flex: 1;
  min-width: 0;
}

.e-toast__title {
  margin: 0;
  color: var(--erpx-color-text-primary);
  font-size: var(--erpx-text-sm);
  font-weight: var(--erpx-weight-medium);
  line-height: var(--erpx-leading-snug);
}

.e-toast__description {
  margin: var(--erpx-space-1) 0 0;
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-xs);
  line-height: var(--erpx-leading-snug);
}

.e-toast__action {
  padding: var(--erpx-space-1) var(--erpx-space-2);
  background: none;
  border: 1px solid var(--erpx-color-border-default);
  border-radius: var(--erpx-radius-sm);
  color: var(--erpx-color-text-primary);
  font: inherit;
  font-size: var(--erpx-text-xs);
  font-weight: var(--erpx-weight-medium);
  cursor: pointer;
}

.e-toast__action:focus-visible,
.e-toast__close:focus-visible {
  outline: none;
  box-shadow: var(--erpx-focus-ring);
}

.e-toast__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: none;
  border: none;
  border-radius: var(--erpx-radius-sm);
  color: var(--erpx-color-text-muted);
  cursor: pointer;
}

.e-toast__close:hover {
  background-color: var(--erpx-color-surface-hover);
  color: var(--erpx-color-text-primary);
}

@keyframes e-toast-in {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes e-toast-out {
  from { opacity: 1; }
  to { opacity: 0; }
}

@media (prefers-reduced-motion: reduce) {
  .e-toast {
    animation: none;
  }
}
</style>
